import { CheckCircle2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";

import type { ProductionMenu } from "./production-utils";
import type { ProductionActualOutput } from "@artisancode/api-types";

interface Props {
  menus: ProductionMenu[];
  isCompleting?: boolean;
  onConfirm: (actualOutputs: ProductionActualOutput[]) => void;
  onCancel: () => void;
}

export function ProductionCompleteConfirm({
  menus,
  isCompleting,
  onConfirm,
  onCancel,
}: Props) {
  const [qtyByLine, setQtyByLine] = useState<
    Record<string, number | undefined>
  >(() =>
    Object.fromEntries(menus.map((m) => [m.outputLine.id, m.outputLine.qty])),
  );
  const isValid = menus.every((m) => (qtyByLine[m.outputLine.id] ?? 0) > 0);

  return (
    <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-primary">
        <CheckCircle2 className="h-4 w-4" />
        Konfirmasi Penyelesaian Produksi
      </div>
      <p className="text-xs text-muted-foreground">
        Bahan baku akan dipotong dari saldo stok toko (bar/gudang) dan stok
        produk jadi akan otomatis ditambahkan ke stok toko.
      </p>
      <div className="space-y-2">
        {menus.map(({ outputLine }) => (
          <div
            key={outputLine.id}
            className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
          >
            <Label
              htmlFor={`actual-output-${outputLine.id}`}
              className="shrink-0 text-xs sm:w-48"
            >
              Jumlah aktual {outputLine.itemName}:
            </Label>
            <div className="flex items-center gap-2">
              <NumberInput
                id={`actual-output-${outputLine.id}`}
                value={qtyByLine[outputLine.id]}
                onChange={(n) =>
                  setQtyByLine((prev) => ({
                    ...prev,
                    [outputLine.id]: n || undefined,
                  }))
                }
                className="h-10 w-32 text-xs font-semibold sm:h-8"
              />
              <span className="text-xs font-medium">{outputLine.uomName}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col-reverse justify-end gap-2 pt-2 sm:flex-row">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          className="min-h-[40px] sm:min-h-9"
        >
          Batal
        </Button>
        <Button
          size="sm"
          onClick={() =>
            isValid &&
            onConfirm(
              menus.map((m) => ({
                lineId: m.outputLine.id,
                qty: qtyByLine[m.outputLine.id] ?? m.outputLine.qty,
              })),
            )
          }
          disabled={isCompleting || !isValid}
          className="min-h-[40px] sm:min-h-9"
        >
          {isCompleting ? "Menyimpan..." : "Eksekusi ke Kartu Stok"}
        </Button>
      </div>
    </div>
  );
}
